// Minimal dependency-free ZIP writer ("store" method, no compression).
// Used to bundle already-compressed images (PNG/JPEG) into one download, so compressing again would only cost time.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
};

const readBytes = (blob) =>
  typeof blob.arrayBuffer === "function"
    ? blob.arrayBuffer().then((buf) => new Uint8Array(buf))
    : new Promise((resolve, reject) => {
        // older browsers (and jsdom) have no Blob.arrayBuffer()
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result));
        reader.onerror = () => reject(reader.error || new Error("Couldn't read a file for the ZIP."));
        reader.readAsArrayBuffer(blob);
      });

/**
 * Builds a .zip from files.
 * @param {{ name: string, blob: Blob }[]} files  names are used as-is (keep them unique)
 * @returns {Promise<Blob>} application/zip
 */
export const buildZip = async (files) => {
  if (files.length > 0xfffe) throw new Error("Too many files for one ZIP.");
  const encoder = new TextEncoder();
  const now = new Date();
  const dosTime = ((now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1)) & 0xffff;
  const dosDate = ((((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate()) & 0xffff) >>> 0;

  const body = [];
  const central = [];
  let offset = 0;

  for (const file of files) {
    const data = await readBytes(file.blob);
    const name = encoder.encode(file.name);
    const crc = crc32(data);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); // local file header
    local.setUint16(4, 20, true); // version needed
    local.setUint16(6, 0x0800, true); // UTF-8 file names
    local.setUint16(8, 0, true); // method: store
    local.setUint16(10, dosTime, true);
    local.setUint16(12, dosDate, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    body.push(local.buffer, name, data);

    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true); // central directory header
    entry.setUint16(4, 20, true); // version made by
    entry.setUint16(6, 20, true); // version needed
    entry.setUint16(8, 0x0800, true);
    entry.setUint16(10, 0, true);
    entry.setUint16(12, dosTime, true);
    entry.setUint16(14, dosDate, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, data.length, true);
    entry.setUint32(24, data.length, true);
    entry.setUint16(28, name.length, true);
    entry.setUint16(30, 0, true); // extra length
    entry.setUint16(32, 0, true); // comment length
    entry.setUint16(34, 0, true); // disk number
    entry.setUint16(36, 0, true); // internal attrs
    entry.setUint32(38, 0, true); // external attrs
    entry.setUint32(42, offset, true); // local header offset
    central.push(entry.buffer, name);

    offset += 30 + name.length + data.length;
  }

  const centralSize = central.reduce((n, part) => n + (part.byteLength ?? part.length), 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); // end of central directory
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);

  return new Blob([...body, ...central, end.buffer], { type: "application/zip" });
};
